// E-Ticaret Siparişleri — basic liste + detay modal + durum güncelleme
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button, Modal, ListPageHeader, EmptyState, FilterBar } from '../components/ui';
import { toast } from '../components/Toast';
import { fmtAmount } from '../utils/numberFormat';

const STATUSES = [
  { v: 'pending',   l: 'Beklemede',  c: '#f59e0b' },
  { v: 'confirmed', l: 'Onaylandı',  c: '#3b82f6' },
  { v: 'preparing', l: 'Hazırlanıyor',c: '#06b6d4' },
  { v: 'shipped',   l: 'Kargoda',    c: '#10b981' },
  { v: 'delivered', l: 'Teslim',     c: '#16a34a' },
  { v: 'cancelled', l: 'İptal',      c: '#dc2626' },
];

export default function EcomOrdersPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: '' });
  const [showFilter, setShowFilter] = useState(false);
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState([]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/ecom/orders', { params: { search, ...filters } });
      if (r.data.success) setList(r.data.data || []);
    } catch (e) { toast.error('Liste yüklenemedi'); } finally { setLoading(false); }
  }, [search, filters]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openDetail = async (o) => {
    try {
      const r = await api.get('/ecom/orders/' + o.id);
      if (r.data.success) {
        setSelected(r.data.data);
        setItems(r.data.data.items || []);
      }
    } catch (e) { toast.error('Açılamadı'); }
  };

  const updateStatus = async (newStatus) => {
    try {
      await api.patch('/ecom/orders/' + selected.id + '/status', { status: newStatus });
      toast.success('Durum güncellendi');
      setSelected(prev => ({ ...prev, status: newStatus }));
      fetchList();
    } catch (e) { toast.error('Güncellenemedi'); }
  };

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="E-Ticaret Siparişleri"
        unit="sipariş"
        count={list.length}
        searchValue={search}
        onSearch={setSearch}
        searchPlaceholder="Sipariş no, müşteri, e-posta..."
        showFilterButton
        filtersActive={showFilter}
        onToggleFilters={() => setShowFilter(s => !s)}
        onRefresh={fetchList}
        refreshing={loading}
      />

      <FilterBar
        active={showFilter}
        onToggle={() => setShowFilter(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'status', type: 'select', label: 'Durum',
            options: [{ value: '', label: 'Tümü' }, ...STATUSES.map(s => ({ value: s.v, label: s.l }))] },
        ]}
      />

      {!loading && list.length === 0 ? (
        <EmptyState icon="🛒" title="Henüz e-ticaret siparişi yok" description="Public API üzerinden sipariş geldiğinde burada görünür." />
      ) : (
        <div style={{ padding: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
            {list.map(o => {
              const st = STATUSES.find(s => s.v === o.status) || STATUSES[0];
              return (
                <div key={o.id} style={S.card} onClick={() => openDetail(o)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <strong style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 'var(--text-sm)' }}>{o.order_number}</strong>
                    <span style={{ ...S.statusBadge, background: st.c + '22', color: st.c }}>{st.l}</span>
                  </div>
                  <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>{o.customer_name}</div>
                  {o.customer_email && <div style={S.sub}>📧 {o.customer_email}</div>}
                  {o.customer_phone && <div style={S.sub}>📞 {o.customer_phone}</div>}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                    <span style={S.sub}>{new Date(o.created_at).toLocaleString('tr-TR')}</span>
                    <strong style={{ color: 'var(--accent-primary)' }}>{fmtAmount(o.total_amount)} ₺</strong>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selected && (
        <Modal
          title={'Sipariş ' + selected.order_number}
          size="lg"
          onClose={() => setSelected(null)}
          footer={
            <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" onClick={() => setSelected(null)}>Kapat</Button>
            </div>
          }
        >
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Durum</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {STATUSES.map(s => (
                <button key={s.v} onClick={() => updateStatus(s.v)}
                  style={{
                    padding: '4px 10px', fontSize: 'var(--text-xs)', fontWeight: 600,
                    border: '1px solid ' + (selected.status === s.v ? s.c : 'var(--border-input)'),
                    background: selected.status === s.v ? s.c + '22' : 'var(--bg-card)',
                    color: selected.status === s.v ? s.c : 'var(--text-secondary)',
                    borderRadius: 'var(--r-full)', cursor: 'pointer',
                  }}>{s.l}</button>
              ))}
            </div>
          </div>

          <div className="form-grid" style={{ marginBottom: 14 }}>
            <div><strong>Müşteri:</strong> {selected.customer_name}</div>
            <div><strong>E-posta:</strong> {selected.customer_email || '—'}</div>
            <div><strong>Telefon:</strong> {selected.customer_phone || '—'}</div>
            <div><strong>Ödeme:</strong> {selected.payment_method || '—'} ({selected.payment_status})</div>
            {selected.shipping_address && (
              <div style={{ gridColumn: '1/-1' }}><strong>Adres:</strong> {selected.shipping_address}</div>
            )}
          </div>

          <h4 style={{ margin: '14px 0 6px', fontSize: 'var(--text-sm)' }}>Sipariş Kalemleri</h4>
          <table style={{ width: '100%', fontSize: 'var(--text-sm)' }}>
            <thead><tr style={{ background: 'var(--bg-tertiary)' }}>
              <th style={{ textAlign: 'left', padding: 8 }}>Ürün</th>
              <th style={{ textAlign: 'right', padding: 8 }}>Adet</th>
              <th style={{ textAlign: 'right', padding: 8 }}>Birim</th>
              <th style={{ textAlign: 'right', padding: 8 }}>Toplam</th>
            </tr></thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id}>
                  <td style={{ padding: 8 }}>{it.stock_name}</td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{it.quantity}</td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{fmtAmount(it.unit_price)}</td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{fmtAmount(it.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr><td colSpan={3} style={{ padding: 8, textAlign: 'right', fontWeight: 700 }}>Toplam:</td>
                  <td style={{ padding: 8, textAlign: 'right', fontWeight: 700, color: 'var(--accent-primary)' }}>{fmtAmount(selected.total_amount)} ₺</td></tr>
            </tfoot>
          </table>
        </Modal>
      )}
    </div>
  );
}

const S = {
  card: {
    padding: 12,
    background: 'var(--bg-card)',
    border: '1px solid var(--border-secondary)',
    borderRadius: 'var(--r-md)',
    cursor: 'pointer',
    boxShadow: 'var(--shadow-xs)',
    transition: 'box-shadow 0.12s',
  },
  statusBadge: {
    fontSize: '0.65rem', fontWeight: 700,
    padding: '2px 8px', borderRadius: 'var(--r-full)',
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  sub: { fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: 2 },
};
